import React, { useCallback, useEffect, useState } from "react";
import { Modal } from "../../widgets/modal";
import { BENCHMARK_SINGLEMAP } from "../../constants/app.constants";
import {
  updateSingleMapScheduled,
  fetchMapScheduled,
} from "./scheduling.utils";
import { useQuery } from "@tanstack/react-query";
import { FC } from "react";
import Swal from "sweetalert2";
import Loader from "../../widgets/Loader";
import TextArea from "../../widgets/textArea";

type OverviewModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: () => void;
  mapId: number;
};

type UpdatedValue = string | Date;

const SchedulingRequestChangeModal: FC<OverviewModalProps> = (props) => {
  const { mapId, isOpen, onClose, onSubmit } = props;

  const [propertyName, setPropertyName] = useState("");
  const [addressBlock, setAddressBlock] = useState("");
  const [companyOwnerContact, setCompanyOwnerContact] = useState("");
  const [companyOwnerContactMobilePhone, setCompanyOwnerContactMobilePhone] =
    useState("");
  const [companyOwnerContactPhone, setCompanyOwnerContactPhone] = useState("");
  const [companyOwnerContactEmail, setCompanyOwnerContactEmail] = useState("");
  const [companyMap, setCompanyMap] = useState("");
  const [companyRequestChange, setCompanyRequestChange] = useState("");
  const [isShowLoader, setIsShowLoader] = useState(false);
  const [validationMsg, setValidationMsg] = useState("");

  const shouldFetch = isOpen && !!mapId;

  const {
    isFetching,
    data: dataBenchmarkSingleMap,
    refetch,
  } = useQuery({
    queryKey: [BENCHMARK_SINGLEMAP, mapId],
    queryFn: () => fetchMapScheduled(mapId),
    enabled: shouldFetch,
    refetchOnWindowFocus: false,
  });

  const resetFields = () => {

    setPropertyName("");
    setAddressBlock("");
    setCompanyOwnerContact("");
    setCompanyOwnerContactMobilePhone("");
    setCompanyOwnerContactPhone("");
    setCompanyOwnerContactEmail("");
    setCompanyMap("");
    setCompanyRequestChange("");
    setValidationMsg("");
    setIsShowLoader(false);
  };

  useEffect(() => {
    if (isOpen && mapId) {
      refetch();
    }
  }, [isOpen, mapId, refetch]);

  useEffect(() => {
    if (dataBenchmarkSingleMap && !isFetching) {
      setPropertyName(dataBenchmarkSingleMap.PropertyName || "");
      setAddressBlock(dataBenchmarkSingleMap.AddressBlock || "");
      setCompanyOwnerContact(dataBenchmarkSingleMap.CompanyOwnerContact || "");
      setCompanyOwnerContactMobilePhone(
        dataBenchmarkSingleMap.CompanyOwnerContactMobilePhone || ""
      );
      setCompanyOwnerContactPhone(
        dataBenchmarkSingleMap.CompanyOwnerContactPhone || ""
      );
      setCompanyOwnerContactEmail(
        dataBenchmarkSingleMap.CompanyOwnerContactEmail || ""
      );
      setCompanyMap(dataBenchmarkSingleMap.CompanyMap || "");
      setCompanyRequestChange(
        dataBenchmarkSingleMap.CompanyRequestChangeMap || ""
      );
    }
  }, [dataBenchmarkSingleMap, isFetching]);

  const onChangeFieldValues = useCallback(
    (id: string, updatedValue: UpdatedValue) => {
      if (id === "companyRequestChange") {
        setCompanyRequestChange(updatedValue as string);
      }
    },
    []
  );

  const handleSubmit = async () => {
    if (!companyRequestChange.trim()) {
      setValidationMsg("Please Enter Change Request");
      return;
    }

    if (mapId) {
      setIsShowLoader(true);
      const updateResult = await updateSingleMapScheduled(
        mapId,
        companyRequestChange
      );
      setIsShowLoader(false);

      if (updateResult) {
        Swal.fire({
          customClass: {
            popup: "sweetAlert",
          },
          icon: "success",
          showConfirmButton: false,
          timer: 1500,
          title: "Request Sent Successfully!",
        });
        setCompanyRequestChange("");
        onSubmit();
      } else {
        Swal.fire({
          customClass: ".swal2-confirm",
          icon: "warning",
          title: "Failed to Send",
        });
      }
    }
  };

  const handleCancel = () => {
    resetFields();
    onClose();
  };

  return (
    
  <Modal show={isOpen} label="Map" onClose={handleCancel}>
    {validationMsg && <div className="text-danger">{validationMsg}</div>}

    <div className="requestModalSection" style={{ position: "relative" }}>
      <div className="modalForm">
        <div className="modal-body" style={{ minHeight: "400px" }}>
          <form>
            <div className="row">
              <div className="col-md-6">
                <div className="form-group row mb-3">
                  <div className="col-md-8 fw-bold mt-2">{propertyName}</div>
                </div>
                <div className="form-group row mb-3">
                  <div className="col-md-8">{addressBlock}</div>
                </div>
                <div className="form-group row mb-3">
                  <label className="col-md-4 col-form-label fw-bold">
                    Ownership:
                  </label>
                  <div className="col-md-8 fw-bold mt-2">{companyOwnerContact}</div>
                </div>
                <div className="form-group row mb-3">
                  <label className="col-md-4 col-form-label">Cell Phone:</label>
                  <div className="col-md-8 mt-2">{companyOwnerContactMobilePhone}</div>
                </div>
                <div className="form-group row mb-3">
                  <label className="col-md-4 col-form-label">Office Phone:</label>
                  <div className="col-md-8 mt-2">{companyOwnerContactPhone}</div>
                </div>
                <div className="form-group row mb-3">
                  <label className="col-md-4 col-form-label">Email:</label>
                  <div className="col-md-8 mt-2">{companyOwnerContactEmail}</div>
                </div>
              </div>

              <div className="col-md-6">
                <div className="form-group row mb-3">
                  <div className="col-md-8 fw-bold">
                    {companyMap && (
                      <img
                        src={companyMap}
                        alt="Company Map"
                        className="custom-img"
                      />
                    )}
                  </div>
                </div>
              </div>

              <div className="row order-md-2">
                <div className="form-group row mb-3">
                  <label className="col-md-4 col-form-label fw-bold lblMap">
                    Request to Change Map:
                    <span className="text-danger">*</span>
                  </label>
                  <div className="col-md-8 mt-2 w-80 textareaMap">
                    <TextArea
                      id="companyRequestChange"
                      className="form-control textareaInput"
                      onChange={onChangeFieldValues}
                      value={companyRequestChange}
                      rows={4}
                    />
                  </div>
                </div>
              </div>

              <div className="row order-md-4">
                <div className="form-group row">
                  <div className="col-md-2"></div>
                  <div className="col-md-8 ms-md-2 actionButtons">
                    <button
                      type="button"
                      className="btn cancelPwdBtn mb-3"
                      onClick={handleCancel}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="btn submitBtn mb-3"
                      onClick={handleSubmit}
                    >
                      Request Change
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </form>

          {(isShowLoader || isFetching) && (
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                backgroundColor: "rgba(255, 255, 255, 0.6)",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                zIndex: 10,
              }}
            >
              <Loader />
            </div>
          )}
        </div>
      </div>
    </div>
  </Modal>
);

  
};

export default SchedulingRequestChangeModal;
